import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bulk_approved, get_bulk_data_detail, get_bulk_items } from '../../reduxData/user/userAction';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Avatar, Button } from "@mui/joy";
import { Spinner } from "react-bootstrap";
import {
    ArrowLeft,
    CheckCircle2,
    Download,
    Package,
    RefreshCw,
    Rows3,
    Trash2,
    XCircle,
} from "lucide-react";
import CustomPagination from '../../components/CustomPagination';
import EmptyData from '../../components/EmptyData';
import { formatDateTime } from '../../hooks/formatDateTime';
import { formatPrice } from '../../hooks/formatPrice';
import { languageFields } from "../../hooks/LanguageFields";
import { getTranslatedText } from "../../hooks/useTextTranslate";
import { useTranslation } from 'react-i18next';

const PRODUCT_STATUS_ALIASES = {
    PENDING: "PRODUCT_PENDING_APPROVAL",
    LIVE: "PRODUCT_LIVE",
    PRODUCT_PAUSED: "PRODUCT_ADMIN_PAUSED",
};

const normalizeProductStatus = (status) => {
    const normalizedStatus = typeof status === "string" ? status.trim().toUpperCase() : status;
    return PRODUCT_STATUS_ALIASES[normalizedStatus] || normalizedStatus;
};

const getProductStatusClass = (status) =>
    normalizeProductStatus(status)?.toLowerCase().replace(/[\s_]+/g, "-") || "";

const BulkUpdatedDetail = () => {
    const { t, i18n } = useTranslation();
    const { bulkDataDetail, totalBulkItems, bulkItems } = useSelector((state) => state.user);
    const { userPermissions } = useSelector((state) => state.auth);
    const location = useLocation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const bulk_id = location?.state?.id || id;
    const { REACT_APP_IMAGE_URL } = process.env;

    const [selectedTab, setSelectedTab] = useState("PRODUCT_PENDING_APPROVAL");
    const [selectedIds, setSelectedIds] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);

    const canApprove = userPermissions?.products?.edit || userPermissions?.bulk_upload?.edit;

    const getTotalRows = (value) => {
        if (!value) return 0;
        return typeof value === "object" ? value.seconds || 0 : value;
    };

    const getFullImageUrl = (url) => {
        const checkExternalUrl = url ? (url?.startsWith("http") || url?.startsWith("https")) : false;
        return checkExternalUrl ? url : url ? `${REACT_APP_IMAGE_URL}${url}` : "";
    };

    const getItemId = (item) => item?.id || item?._id;

    const getItemImage = (item) => {
        return (
            item?.variants?.[0]?.images?.[0] ||
            item?.variant_auction?.[0]?.images?.[0] ||
            item?.thumbnail ||
            ""
        );
    };

    const fetchItems = async (page = currentPage, limit = perPage, status = selectedTab) => {
        if (!bulk_id) return;
        await get_bulk_items(dispatch, {
            bulk_id,
            selectedTab: status,
            page,
            limit,
        });
    };

    const fetchDetail = async () => {
        if (!bulk_id) return;
        await get_bulk_data_detail(dispatch, { bulk_id });
    };

    const refreshPage = async () => {
        await fetchDetail();
        await fetchItems(currentPage, perPage, selectedTab);
    };

    useEffect(() => {
        fetchDetail();
    }, [bulk_id]);

    useEffect(() => {
        setSelectedIds([]);
        fetchItems(currentPage, perPage, selectedTab);
    }, [bulk_id, selectedTab, currentPage, perPage]);

    const statCards = useMemo(() => ([
        {
            id: "totalRows",
            labelKey: "bulkUploadDetail.statTotalRows",
            value: getTotalRows(bulkDataDetail?.total_rows),
            icon: Rows3,
            tone: "neutral",
        },
        {
            id: "successRows",
            labelKey: "bulkUploadDetail.statSuccessRows",
            value: bulkDataDetail?.success_rows || 0,
            icon: CheckCircle2,
            tone: "success",
        },
        {
            id: "rejectedRows",
            labelKey: "bulkUploadDetail.statRejectedRows",
            value: bulkDataDetail?.rejected_rows || 0,
            icon: XCircle,
            tone: "danger",
        },
        {
            id: "productCreated",
            labelKey: "bulkUploadDetail.statProductCreated",
            value: bulkDataDetail?.product_created || 0,
            icon: Package,
            tone: "primary",
        },
        {
            id: "productUpdated",
            labelKey: "bulkUploadDetail.statProductUpdated",
            value: bulkDataDetail?.product_updated || 0,
            icon: RefreshCw,
            tone: "info",
        },
        {
            id: "productRejected",
            labelKey: "bulkUploadDetail.statProductRejected",
            value: bulkDataDetail?.product_rejected || 0,
            icon: XCircle,
            tone: "warning",
        },
        {
            id: "variantCreated",
            labelKey: "bulkUploadDetail.statVariantCreated",
            value: bulkDataDetail?.variant_created || 0,
            icon: Package,
            tone: "accent",
        },
        {
            id: "productDeleted",
            labelKey: "bulkUploadDetail.statProductDeleted",
            value: bulkDataDetail?.product_deleted || 0,
            icon: Trash2,
            tone: "danger",
        },
    ]), [bulkDataDetail]);

    const tabs = [
        { labelKey: "bulkUploadDetail.tabPending", value: "PRODUCT_PENDING_APPROVAL" },
        { labelKey: "bulkUploadDetail.tabApprove", value: "PRODUCT_LIVE" },
    ];

    const toggleSelectItem = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        const visibleIds = (bulkItems || []).map(getItemId).filter(Boolean);
        const allSelected = visibleIds.length > 0 && visibleIds.every((itemId) => selectedIds.includes(itemId));

        if (allSelected) {
            setSelectedIds((prev) => prev.filter((itemId) => !visibleIds.includes(itemId)));
        } else {
            setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
        }
    };

    const getStatusLabel = (status) => {
        const normalizedStatus = normalizeProductStatus(status);
        const statusTranslationKey = `itemListing.statuses.${normalizedStatus}`;
        const translatedStatus = t(statusTranslationKey);
        return translatedStatus === statusTranslationKey
            ? normalizedStatus?.replace(/_/g, " ")
            : translatedStatus;
    };

    const isAllSelected = () => {
        const visibleIds = (bulkItems || []).map(getItemId).filter(Boolean);
        return visibleIds.length > 0 && visibleIds.every((itemId) => selectedIds.includes(itemId));
    };

    const handleBulkApprove = async () => {
        if (selectedIds.length === 0) return;
        try {
            setIsLoading(true);
            const res = await bulk_approved(dispatch, {
                item_ids: selectedIds,
                status: "PRODUCT_LIVE",
            });

            if (res?.data) {
                setSelectedIds([]);
                await refreshPage();
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (page, rows) => {
        setCurrentPage(page);
        setPerPage(rows);
    };

    const getItemTypeOptions = (isDutch) => (
        isDutch ? ["Artikel", "Variant", "Eenvoudig"] : ["Item", "Variant", "Simple"]
    );

    const getFinalSaleOptions = (isDutch) => (
        isDutch ? ["Ja", "Nee"] : ["Yes", "No"]
    );

    const getConditionOptions = (isDutch) => [
        isDutch ? "Nieuw" : "New",
        isDutch ? "Nieuw (met kaartje)" : "New (with tag)",
        isDutch ? "Nieuw (zonder kaartjes)" : "New (without tags)",
    ];

    const getGenderOptions = (isDutch) => (
        isDutch ? ["Mannen", "Vrouwen", "Kinderen", "Unisex"] : ["Men", "Women", "Kids", "Unisex"]
    );

    const convertRowsToCsv = (rows) => {
        if (!rows?.length) return "";

        const headers = Object.keys(rows[0]);
        const escapeCsvValue = (value) => {
            const normalizedValue = value === undefined || value === null ? "" : String(value);
            return `"${normalizedValue.replace(/"/g, '""')}"`;
        };

        return [
            headers.map(escapeCsvValue).join(","),
            ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(",")),
        ].join("\n");
    };

    const downloadErrorCsv = async (errorRows, itemId) => {
        if (!errorRows?.length) return;

        try {
            setDownloadingId(itemId);
            const isDutch = i18n?.language === "nl";
            const sortedErrors = [...errorRows].sort(
                (a, b) => (a?.rowData?.row || 0) - (b?.rowData?.row || 0)
            );
            const finalRows = await Promise.all(
                sortedErrors.map(async (item) => {
                    const rowData = item?.rowData || {};
                    const translatedError = await getTranslatedText(item?.error, i18n.language);
                    const translatedColor = rowData?.color_name
                        ? await getTranslatedText(rowData.color_name, i18n.language)
                        : "";

                    return {
                        [isDutch ? "foutmelding" : "error message"]: translatedError || "",
                        [isDutch ? "rijnummer" : "row number"]: Number(rowData?.row || 0),
                        item_type: rowData?.item_type === "Item" ? getItemTypeOptions(isDutch)[0] : rowData?.item_type === "Variant" ? getItemTypeOptions(isDutch)[1] : rowData?.item_type === "Simple" ? getItemTypeOptions(isDutch)[2] : "",
                        [isDutch ? "titel" : "title"]: languageFields(rowData, "title", i18n) || "",
                        [isDutch ? "artikelnummer" : "article_number"]: rowData?.article_number || "",
                        [isDutch ? "beschrijving" : "description"]: languageFields(rowData, "discription", i18n) || "",
                        [isDutch ? "merk" : "brand"]: rowData?.brand_name || "",
                        [isDutch ? "categorie_naam" : "category_name"]: languageFields(rowData, "category_name", i18n) || "",
                        [isDutch ? "subcategorie_naam" : "subcategory_name"]: languageFields(rowData, "subcategory_name", i18n) || "",
                        [isDutch ? "opnemen_in_eindverkoop" : "include_in_final_sale"]:
                            rowData?.include_in_final_sale === "Yes"
                                ? getFinalSaleOptions(isDutch)[0]
                                : rowData?.include_in_final_sale === "No"
                                    ? getFinalSaleOptions(isDutch)[1]
                                    : "",
                        [isDutch ? "startprijs_eindverkoop" : "Final Sale start price"]: rowData?.minimum_start_price || "",
                        [isDutch ? "winkelprijs" : "Shop price"]: rowData?.original_retail_price || "",
                        [isDutch ? "conditie" : "condition"]:
                            rowData?.condition === "New"
                                ? getConditionOptions(isDutch)[0]
                                : rowData?.condition === "New (with tag)"
                                    ? getConditionOptions(isDutch)[1]
                                    : rowData?.condition === "New (without tags)"
                                        ? getConditionOptions(isDutch)[2]
                                        : "",
                        [isDutch ? "maatsysteem" : "size_system"]: rowData?.size_system || "",
                        [isDutch ? "geslacht" : "gender"]:
                            rowData?.gender === "Men"
                                ? getGenderOptions(isDutch)[0]
                                : rowData?.gender === "Women"
                                    ? getGenderOptions(isDutch)[1]
                                    : rowData?.gender === "Kids"
                                        ? getGenderOptions(isDutch)[2]
                                        : rowData?.gender === "Unisex"
                                            ? getGenderOptions(isDutch)[3]
                                            : "",
                        [isDutch ? "kleurnaam" : "color name"]: translatedColor,
                        [isDutch ? "maatnaam" : "size_name"]: rowData?.size_name || "",
                        [isDutch ? "aantal" : "quantity"]: rowData?.quantity || "",
                        [isDutch ? "afbeeldingen" : "images"]: rowData?.images || "",
                    };
                })
            );

            const csvContent = convertRowsToCsv(finalRows);
            const blob = new Blob([`\uFEFF${csvContent}`], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `upload-errors-${Date.now()}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setDownloadingId(null);
        }
    };

    const isDownloadingErrors = downloadingId === (bulkDataDetail?.id || bulk_id);
    const isImportCancelled = bulkDataDetail?.status === "IMPORT_CANCELLED";

    return (
        <div className="container bulk-detail-page">
            <div className="bulk-detail-header">
                <div>
                    <button className="bulk-back-btn" onClick={() => navigate("/bulk_upload")}>
                        <ArrowLeft size={18} />
                        {t("bulkUploadDetail.backButton")}
                    </button>
                    <h2>
                        {t("bulkUploadDetail.pageTitle")}
                        <span className={`status-badge ${isImportCancelled ? "cancelled" : "approved"} bulk-detail-status-badge`}>
                            {isImportCancelled ? t("bulkUploadDetail.statusCancelled") : t("bulkUploadDetail.statusCompleted")}
                        </span>
                    </h2>
                    <p>
                        {t("bulkUploadDetail.uploadIdLabel", { id: bulkDataDetail?.id || bulk_id })}
                        {bulkDataDetail?.created_at
                            ? ` ${t("bulkUploadDetail.createdOnLabel", { date: formatDateTime(bulkDataDetail.created_at, i18n) })}`
                            : ""}
                    </p>
                </div>
                <div className="bulk-detail-actions">
                    <button
                        className="bulk-download-btn"
                        onClick={() => downloadErrorCsv(bulkDataDetail?.rejected_rows_data || [], bulkDataDetail?.id || bulk_id)}
                        disabled={!bulkDataDetail?.rejected_rows_data?.length || isDownloadingErrors}
                    >
                        {isDownloadingErrors ? <Spinner animation="border" size="sm" /> : <Download size={17} />}
                        {isDownloadingErrors ? t("bulkUploadDetail.downloadingLabel") : t("bulkUploadDetail.downloadRejectedBtn")}
                    </button>
                </div>
            </div>

            <div className="bulk-stats-grid">
                {statCards.map(({ id, labelKey, value, icon: Icon, tone }) => (
                    <div className={`bulk-stat-card ${tone}`} key={id}>
                        <div className="bulk-stat-icon">
                            <Icon size={22} />
                        </div>
                        <div>
                            <span>{t(labelKey)}</span>
                            <strong>{value}</strong>
                        </div>
                    </div>
                ))}
            </div>

            <div className="bulk-items-box">
                <div className="bulk-items-topbar">
                    <div>
                        <h4>{t("bulkUploadDetail.itemsSectionTitle")}</h4>
                        <p>{t("bulkUploadDetail.itemsFoundText", { count: totalBulkItems || 0, status: getStatusLabel(selectedTab) })}</p>
                    </div>
                    <div className="bulk-tabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.value}
                                className={selectedTab === tab.value ? "active" : ""}
                                onClick={() => {
                                    setSelectedTab(tab.value);
                                    setCurrentPage(1);
                                }}
                            >
                                {t(tab.labelKey)}
                            </button>
                        ))}
                    </div>
                </div>

                {selectedTab === "PRODUCT_PENDING_APPROVAL" && selectedIds.length > 0 && (
                    <div className="bulk-selection-bar">
                        <strong>{selectedIds.length}</strong>
                        <span>{t("bulkUploadDetail.selectedLabel")}</span>
                        <div>
                            <button className="bulk-approve-btn" onClick={handleBulkApprove} disabled={isLoading || !canApprove}>
                                <CheckCircle2 size={16} />
                                {isLoading ? t("bulkUploadDetail.approvingLabel") : t("bulkUploadDetail.approveSelectedBtn")}
                            </button>
                            <button className="bulk-clear-selection" onClick={() => setSelectedIds([])}>
                                {t("bulkUploadDetail.clearBtn")}
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-responsive bulk-item-table-wrap">
                    <table className="table table-hover user-management-table bulk-item-table">
                        <thead>
                            <tr>
                                {selectedTab === "PRODUCT_PENDING_APPROVAL" && canApprove && (
                                    <th className="text-center bulk-check-col">
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected()}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                )}
                                <th>{t("bulkUploadDetail.colThumbnail")}</th>
                                <th>{t("bulkUploadDetail.colItemName")}</th>
                                <th>{t("bulkUploadDetail.colSeller")}</th>
                                <th>{t("bulkUploadDetail.colCategory")}</th>
                                <th>{t("bulkUploadDetail.colCondition")}</th>
                                <th>{t("bulkUploadDetail.colShopPrice")}</th>
                                <th>{t("bulkUploadDetail.colStatus")}</th>
                                <th className="text-center">{t("bulkUploadDetail.colAction")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(bulkItems) && bulkItems.length > 0 ? (
                                bulkItems.map((item, index) => {
                                    const itemId = getItemId(item);

                                    return (
                                        <tr key={itemId || index}>
                                            {selectedTab === "PRODUCT_PENDING_APPROVAL" && canApprove && (
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedIds.includes(itemId)}
                                                        onChange={() => toggleSelectItem(itemId)}
                                                    />
                                                </td>
                                            )}
                                            <td>
                                                <Avatar
                                                    variant="soft"
                                                    src={getFullImageUrl(getItemImage(item))}
                                                    alt={languageFields(item, "title", i18n) || "Item thumbnail"}
                                                />
                                            </td>
                                            <td className="bulk-item-title">{languageFields(item, "title", i18n) || t("bulkUploadDetail.naFallback")}</td>
                                            <td>{item?.usersdata?.user_name || item?.seller?.user_name || t("bulkUploadDetail.naFallback")}</td>
                                            <td>{languageFields(item, "category_name", i18n) || t("bulkUploadDetail.naFallback")}</td>
                                            <td>{languageFields(item, "condition", i18n) || t("bulkUploadDetail.naFallback")}</td>
                                            <td>
                                                {item?.original_retail_price
                                                    ? formatPrice(item.original_retail_price, i18n)
                                                    : t("bulkUploadDetail.naFallback")}
                                            </td>
                                            <td>
                                                <span className={`status-badge ${getProductStatusClass(item?.status || selectedTab)}`}>
                                                    {getStatusLabel(item?.status || selectedTab)}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant="plain"
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => item?.slug && navigate(`/item-details/${item.slug}`)}
                                                    disabled={!item?.slug}
                                                >
                                                    {t("bulkUploadDetail.viewButton")}
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={selectedTab === "PRODUCT_PENDING_APPROVAL" && canApprove ? 9 : 8} className="text-center py-5">
                                        <EmptyData />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalBulkItems > 0 && (
                    <div className="mt-3">
                        <CustomPagination
                            total={totalBulkItems}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkUpdatedDetail;
